if (!customElements.get('custom-content-slider')) {
  customElements.define(
    'custom-content-slider',
    class CustomContentSlider extends HTMLElement {
      constructor() {
        super();
        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
      }

      connectedCallback() {
        this.container = this.querySelector('[data-swipe-container]');
        if (!this.container) return;

        this.inner = this.container.querySelector('[data-swipe-inner]');
        this.prevBtn = this.querySelector('[data-swipe-prev]');
        this.nextBtn = this.querySelector('[data-swipe-next]');
        this.pagination = this.querySelector('[data-swipe-pagination]');
        this.bulletLabelTemplate = this.dataset.bulletLabel || 'Page __N__';
        this.dots = [];

        this.bindNavButtons();

        if (this.pagination) {
          this.renderDots();
          this.container.addEventListener('scroll', this.handleScroll, { passive: true });
          window.addEventListener('resize', this.handleResize, { passive: true });
        }
      }

      disconnectedCallback() {
        this.container?.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
      }

      getStepWidth() {
        const firstChild = this.inner?.firstElementChild;
        if (firstChild) {
          const gap = parseFloat(getComputedStyle(this.inner).columnGap) || 0;
          return firstChild.offsetWidth + gap;
        }
        return this.container.offsetWidth * 0.24;
      }

      getMaxScroll() {
        return Math.max(0, this.container.scrollWidth - this.container.clientWidth);
      }

      getPageCount() {
        const step = this.getStepWidth();
        const maxScroll = this.getMaxScroll();
        if (!step || maxScroll <= 0) return 0;
        return Math.round(maxScroll / step) + 1;
      }

      bindNavButtons() {
        if (!this.prevBtn || !this.nextBtn) return;
        this.prevBtn.addEventListener('click', () => {
          this.container.scrollBy({ left: -this.getStepWidth(), behavior: 'smooth' });
        });
        this.nextBtn.addEventListener('click', () => {
          this.container.scrollBy({ left: this.getStepWidth(), behavior: 'smooth' });
        });
      }

      renderDots() {
        const pageCount = this.getPageCount();

        if (pageCount < 2) {
          this.pagination.replaceChildren();
          this.pagination.hidden = true;
          this.dots = [];
          return;
        }

        this.pagination.hidden = false;
        const fragment = document.createDocumentFragment();
        this.dots = [];

        for (let i = 0; i < pageCount; i++) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'swiper-pagination-bullet';
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-label', this.bulletLabelTemplate.replace('__N__', i + 1));
          button.dataset.index = String(i);
          button.addEventListener('click', () => this.scrollToIndex(i));
          fragment.appendChild(button);
          this.dots.push(button);
        }

        this.pagination.replaceChildren(fragment);
        this.updateActiveDot();
      }

      handleScroll() {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.updateActiveDot());
      }

      handleResize() {
        this.renderDots();
      }

      updateActiveDot() {
        if (!this.dots.length) return;
        const step = this.getStepWidth();
        if (!step) return;
        const rawIndex = Math.round(this.container.scrollLeft / step);
        const activeIndex = Math.max(0, Math.min(rawIndex, this.dots.length - 1));

        this.dots.forEach((dot, i) => {
          const isActive = i === activeIndex;
          dot.classList.toggle('swiper-pagination-bullet-active', isActive);
          dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
          dot.setAttribute('tabindex', isActive ? '0' : '-1');
        });
      }

      scrollToIndex(index) {
        const step = this.getStepWidth();
        const target = Math.min(index * step, this.getMaxScroll());
        this.container.scrollTo({ left: target, behavior: 'smooth' });
      }
    }
  );
}
